import { useEffect, useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { Loader2, Shield, Gavel } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BidPayPalCheckout({ 
  orderId, 
  bidAmount,
  tierName,
  eventTitle,
  onApprove, 
  onError, 
  onCancel 
}) {
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/payments/paypal/config`);
        setClientId(res.data.client_id);
      } catch (error) {
        console.error('Failed to load PayPal config:', error);
        if (onError) onError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [onError]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="text-center py-8 text-red-500">
        Payment system unavailable. Please try again later.
      </div>
    );
  }

  return (
    <PayPalScriptProvider options={{ 
      "client-id": clientId,
      currency: "USD",
      intent: "capture"
    }}>
      <div className="space-y-4">
        {/* Bid Summary */}
        <div className="bg-secondary/30 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2 text-primary">
            <Gavel className="w-5 h-5" />
            <span className="font-semibold">Bid Payment</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{eventTitle}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tier</span>
              <span>{tierName}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
              <span>Bid Amount</span>
              <span className="text-primary">${bidAmount?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
          <p className="text-amber-200">
            <strong>How auction bidding works:</strong> Your payment will be captured immediately. 
            If someone outbids you, you'll receive an automatic refund to your PayPal account.
          </p>
        </div>

        {/* PayPal Buttons */}
        <div className="min-h-[200px]">
          <PayPalButtons
            style={{ 
              layout: "vertical",
              color: "gold",
              shape: "rect",
              label: "pay"
            }}
            createOrder={() => {
              // Return the existing order ID created by the backend
              return orderId;
            }}
            onApprove={onApprove}
            onError={onError}
            onCancel={onCancel}
          />
        </div>

        {/* Security Note */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Shield className="w-4 h-4" />
          <span>Secure payment powered by PayPal</span>
        </div>
      </div>
    </PayPalScriptProvider>
  );
}
